/**
 * Top HUD bar for /play. Shows blind level, hand number, pot size (with
 * BB subtitle), and the current-turn label. All values optional so the
 * route can render the shell before a tournament starts.
 */
import { toBB } from '@/utils/bb';

export type HudProps = {
  level?: number;
  blinds?: { sb: number; bb: number; ante?: number };
  handNumber?: number;
  pot?: number;
  /** Label of the player whose turn is active (e.g., "당신", "GRIM"). */
  currentTurnLabel?: string | null;
};

export function Hud({ level, blinds, handNumber, pot, currentTurnLabel }: HudProps) {
  const bbSize = blinds?.bb ?? 0;
  return (
    <header
      className="relative flex flex-col gap-1 bg-bg-elev/80 backdrop-blur px-4 py-1.5 border-b-2 border-border"
      data-testid="hud"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="font-display text-sm text-accent-alt text-glow-cyan tracking-wider">
            L{level ?? '–'}
          </span>
          <span className="num text-lg text-fg/90">
            {blinds ? `${blinds.sb}/${blinds.bb}` : '–/–'}
            {blinds?.ante ? ` · A${blinds.ante}` : ''}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-display text-[11px] text-muted tracking-widest uppercase">
            Hand <span className="text-fg num text-base">#{handNumber ?? 0}</span>
          </span>
        </div>
        <div className="flex flex-col items-end leading-tight">
          <div className="flex items-baseline gap-1">
            <span className="font-display text-[11px] text-accent tracking-widest uppercase">
              Pot
            </span>
            <span className="num text-xl text-accent text-glow-blood">{pot ?? 0}</span>
          </div>
          {bbSize > 0 ? (
            <span className="font-display text-[9px] text-accent/80 uppercase tracking-widest">
              {toBB(pot ?? 0, bbSize)}
            </span>
          ) : null}
        </div>
      </div>
      {currentTurnLabel ? (
        <div
          className="font-display text-[11px] text-accent-alt text-glow-cyan text-center tracking-widest uppercase animate-pulse"
          data-testid="hud-turn-indicator"
        >
          ▶ Turn · <span className="text-fg">{currentTurnLabel}</span>
        </div>
      ) : null}
    </header>
  );
}
